<template>
  <div
    class="message-bubble"
    :class="{
      'message-bubble--user': isUser,
      'message-bubble--assistant': !isUser,
      'message-bubble--system': isSystem,
    }"
  >
    <!-- Oliver mini-avatar (assistant messages only) — WebGL orb -->
    <div
      v-if="!isUser && !isSystem"
      class="bubble-avatar bubble-avatar--oliver"
    >
      <Orb :size="64" :colors="orbColors" />
    </div>

    <!-- Bubble body: the status line on the background, then the answer card -->
    <div class="bubble-body">
      <template v-for="part in parts">
        <ReasoningBlock
          v-if="part.type === 'activity'"
          :key="`activity-${part.key}`"
          :activity="part.activity"
        />

        <div
          v-else
          :key="`text-${part.key}`"
          class="message-content"
          :class="{
            'message-content--copyable': !isUser && !isSystem && part.html,
          }"
        >
          <!-- System Message (thinking, loading, etc.) -->
          <div v-if="isSystem" class="system-message">
            <div v-html="renderedContent" class="system-markdown"></div>
          </div>

          <UserMessage v-else-if="isUser" :message="message" />

          <!-- Assistant Message (with markdown) -->
          <div
            v-else
            class="assistant-message"
            :class="{
              'assistant-message--guardrail':
                part.lastCard && message.guardrail,
            }"
          >
            <div
              v-if="part.html"
              v-html="part.html"
              class="markdown-content"
              @error.capture="artifacts.handleMediaError"
              @click="handleContentClick"
              @mouseover="showCard"
              @mouseout="hideCard"
              @focusin="showCard"
              @focusout="hideCard"
            ></div>

            <template v-if="part.lastCard">
              <p v-if="message.cutShort" class="cut-short">
                <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
                The answer was cut short here. What's above is complete up to
                this point.
              </p>

              <GuardrailCard
                v-if="message.guardrail"
                :guardrail="message.guardrail"
              />

              <InteractionHost
                v-if="message.interactions && message.interactions.length"
                :interactions="message.interactions"
                :resolution="message.interactionResolution"
                :conversation-id="conversationId"
                :superseded="!isLast"
                @answered="$emit('interaction-answered')"
              />

              <CitationSources
                v-if="showFooter && citations?.sources.length"
                :open="sourcesOpen"
                @update:open="sourcesOpen = $event"
                :sources="citations.sources"
                :highlighted="highlightedSources"
                :came-from="returnTo ? returnTo.n : null"
                :link-to="linkTo"
                @return="returnToMarker"
                @click="handleContentClick"
              />
            </template>
          </div>

          <!-- A card copies itself from its corner until the footer's copy takes over -->
          <div
            v-if="part.html && !(part.lastCard && showFooter)"
            class="card-actions"
          >
            <button
              class="copy-btn"
              :aria-label="copiedKey === part.key ? 'Copied' : 'Copy'"
              @click="handleCopy(part)"
            >
              <i
                :class="copiedKey === part.key ? 'fas fa-check' : 'far fa-copy'"
              ></i>
            </button>
          </div>

          <!-- Shared footer: status + time left, copy right -->
          <div v-if="part.lastCard && showFooter" class="bubble-footer">
            <div class="bubble-footer-meta">
              <i
                v-if="message.status === 'failed'"
                v-tooltip="message.error || 'Failed to send message'"
                class="
                  fas
                  fa-exclamation-circle
                  message-status-icon message-status-icon--failed
                "
              ></i>
              <i
                v-else-if="message.status === 'pending'"
                v-tooltip="'Sending...'"
                class="
                  fas
                  fa-clock
                  message-status-icon message-status-icon--pending
                "
              ></i>
              <span v-if="showTimestamp" class="message-timestamp">{{
                formattedTime
              }}</span>
            </div>
            <div class="bubble-footer-actions">
              <button
                v-if="isUser || part.html"
                class="copy-btn"
                @click="handleCopy(part)"
              >
                <i
                  :class="
                    copiedKey === part.key ? 'fas fa-check' : 'far fa-copy'
                  "
                ></i>
                <span class="copy-btn-label">{{
                  copiedKey === part.key ? "Copied" : "Copy"
                }}</span>
              </button>
              <!-- Copy gives the text alone; the arrow offers it with its sources. -->
              <Dropdown
                v-if="!isUser && part.html && citations?.sources.length"
                :triggers="['click']"
                :auto-hide="true"
                :distance="6"
                placement="top-end"
                popper-class="oliver-copy-popper"
              >
                <button
                  type="button"
                  class="copy-btn copy-btn--more"
                  aria-label="More ways to copy"
                >
                  <i class="fas fa-chevron-down"></i>
                </button>
                <template #popper="{ hide }">
                  <div class="copy-menu" role="menu" :style="themeVars">
                    <div class="copy-menu__label">Copy</div>
                    <button
                      type="button"
                      role="menuitem"
                      class="copy-menu__item"
                      @click="
                        hide();
                        handleCopy(part);
                      "
                    >
                      <span class="copy-menu__icon"
                        ><i class="far fa-copy"></i
                      ></span>
                      <span class="copy-menu__text">
                        <span class="copy-menu__name">Copy text</span>
                        <span class="copy-menu__hint"
                          >Ready to paste into an email, letter or report</span
                        >
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      class="copy-menu__item"
                      @click="
                        hide();
                        handleCopy(part, { withSources: true });
                      "
                    >
                      <span class="copy-menu__icon"
                        ><i class="fas fa-list-ol"></i
                      ></span>
                      <span class="copy-menu__text">
                        <span class="copy-menu__name">Copy with sources</span>
                        <span class="copy-menu__hint"
                          >Adds [1], [2] and the source list at the end</span
                        >
                      </span>
                    </button>
                  </div>
                </template>
              </Dropdown>

              <!-- Negative-only feedback control. Self-contained: it owns the
                   reason picker, submit/undo logic, and its own gating (renders
                   nothing unless the message is an eligible assistant answer). -->
              <FeedbackPopover
                :message="message"
                :conversation-id="conversationId"
                :is-streaming="isStreaming"
              />
            </div>
          </div>
        </div>

        <!-- Below the answer card, on the chat background: never part of what Copy takes -->
        <!-- eslint-disable-next-line vue/valid-v-for -->
        <transition
          name="follow-ups-fade"
          @before-leave="pinHeight"
          @leave="collapse"
        >
          <FollowUpChips
            v-if="part.lastCard && (followUps.length || followUpsLoading)"
            :key="`follow-ups-${part.key}`"
            :chips="followUps"
            :loading="followUpsLoading"
            @pick="$emit('follow-up', $event)"
          />
        </transition>
      </template>
    </div>

    <!-- User avatar (user messages only) -->
    <div v-if="isUser" class="bubble-avatar bubble-avatar--user">
      <img v-if="userPhoto" :src="userPhoto" alt="You" />
      <div v-else class="bubble-avatar-fallback">
        <i class="fas fa-user"></i>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="card"
        ref="citationCard"
        class="citation-card"
        :class="{ 'citation-card--pinned': card.pinned }"
        role="tooltip"
        :style="card.style"
        @mouseenter="keepCard"
        @mouseleave="hideCard"
      >
        <span class="citation-card__label">{{ card.source.shown }}</span>
        <span v-if="card.source.excerpt" class="citation-card__quote">{{
          card.source.excerpt
        }}</span>
        <span class="citation-card__actions">
          <a
            v-if="card.source.url"
            v-bind="linkAttributes(linkTo(card.source.url))"
            @click="followCardLink"
            >{{ openLabel(card.source) }}</a
          >
          <button type="button" @click="cardToSources">Show in sources</button>
          <button
            v-if="card.pinned"
            type="button"
            class="citation-card__close"
            @click="card = null"
          >
            Close
          </button>
        </span>
      </div>
    </Teleport>
  </div>
</template>

<script>
import Orb from "@/components/OliverBotV2/shared/Orb.vue";
import {
  buildThemeVars,
  getThemeOrbColors,
} from "@/components/OliverBotV2/theme/themes.js";
import dayjs from "dayjs";
import { computed, markRaw } from "vue";
import FeedbackPopover from "@/components/OliverBotV2/chat/FeedbackPopover.vue";
import GuardrailCard from "@/components/OliverBotV2/chat/GuardrailCard.vue";
import InteractionHost from "@/components/OliverBotV2/interactions/InteractionHost.vue";
import ReasoningBlock from "@/components/OliverBotV2/chat/ReasoningBlock.vue";
import UserMessage from "./UserMessage.vue";
import { activityParts } from "@/components/OliverBotV2/utils/activityParts.js";
import {
  linkAttributes,
  routedLink,
  routeExists,
} from "@/components/OliverBotV2/utils/oliverLinks.js";
import CitationSources from "./CitationSources.vue";
import FollowUpChips from "./FollowUpChips.vue";
import { indexCitations, openLabel } from "./citations.js";
import { createRenderer, renderAnswer } from "./renderAnswer.js";
import { htmlForCopy, plainTextForCopy } from "./copyForWord.js";
import { useAnswerArtifacts } from "./useAnswerArtifacts.js";
import { mapState } from "vuex";
import { Dropdown } from "floating-vue";

export default {
  name: "MessageBubble",
  components: {
    CitationSources,
    Dropdown,
    FollowUpChips,
    Orb,
    FeedbackPopover,
    GuardrailCard,
    InteractionHost,
    ReasoningBlock,
    UserMessage,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    showTimestamp: {
      type: Boolean,
      default: true,
    },
    userPhoto: {
      type: String,
      default: null,
    },
    conversationId: {
      type: String,
      default: null,
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether this is the most recent message in the conversation. An
     * unanswered interaction card on an older message is treated as ignored.
     */
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "interaction-answered",
    "open-image",
    "open-table",
    "follow-up",
    "stop-following",
  ],
  setup(props) {
    return { artifacts: useAnswerArtifacts(() => props.message) };
  },
  data() {
    return {
      copiedKey: null,
      // Throttled mirror of message.content used for markdown rendering. Re-parsing
      // and swapping innerHTML on every streamed token causes layout thrash
      // (flicker); we coalesce updates to a smooth cadence instead.
      displayContent: "",
      // The answer's numbered sources, swapped only when one or its quote changes.
      citations: null,
      sourcesOpen: false,
      highlightedSources: [],
      // The number the reader clicked to reach the sources, so its row can lead back to it.
      returnTo: null,
      // The source a hovered or focused number names, and where its card sits.
      card: null,
    };
  },
  computed: {
    ...mapState({
      oliverTheme: (state) => state.oliverChatBotV2.theme,
      oliverDarkMode: (state) => state.oliverChatBotV2.darkMode,
    }),
    // The card is teleported to <body>, outside the element carrying Oliver's theme vars.
    themeVars() {
      return buildThemeVars(this.oliverTheme, this.oliverDarkMode);
    },
    orbColors() {
      return getThemeOrbColors(this.oliverTheme);
    },
    // Rebuilt per conversation, not per token: `renderMarkdown` re-parses several times
    // a second while an answer streams.
    markdownRenderer() {
      const router = this.$router;
      return createRenderer(this.conversationId, (pathname) =>
        routeExists(router, pathname)
      );
    },
    latestCitations() {
      return indexCitations(this.displayContent, {
        attachments: this.artifacts.attachments(),
        citations: this.message.citations,
        idFor: (reference) => this.artifacts.attachmentIdFor(reference),
      });
    },
    linkTo() {
      const router = this.$router;
      return (href) =>
        routedLink(href, this.conversationId, {
          isRoutable: (pathname) => routeExists(router, pathname),
        });
    },
    isUser() {
      return this.message.role === "user";
    },
    isSystem() {
      return this.message.system === true;
    },
    // Shown once an answer has finished, so it never moves while the answer streams.
    showFooter() {
      return !this.isSystem && !(this.isStreaming && !this.isUser);
    },
    // The latest finished, whole answer offers what to ask next; anything else does not.
    followUpsEligible() {
      const m = this.message;
      const outcome = m.activity?.outcome;
      if (this.isUser || !this.isLast || !this.showFooter || m.cutShort)
        return false;
      return outcome !== "failed" && outcome !== "cancelled";
    },
    followUps() {
      const m = this.message;
      if (!this.followUpsEligible) return [];
      return Array.isArray(m.followUps) ? m.followUps : [];
    },
    // Placeholders in the chips' place while they are still being made.
    followUpsLoading() {
      return (
        this.followUpsEligible &&
        this.message.followUpsPending === true &&
        !this.followUps.length
      );
    },
    renderedContent() {
      return this.renderMarkdown(this.displayContent);
    },
    /**
     * The message as shown: the status line, then the answer card, from the throttled mirror.
     * User and system messages are one card. The card carries the message's extras and footer;
     * an answer with no text of its own still gets one when it has an interaction or a
     * guardrail to show. The HTML comes from `partHtml`, so an activity update never re-parses
     * markdown.
     */
    parts() {
      if (this.isUser || this.isSystem) {
        return [{ type: "text", key: "message", lastCard: true }];
      }
      const parts = activityParts({
        content: this.displayContent,
        activity: this.message.activity,
      });
      const html = this.partHtml(parts);
      const lastText = parts.map((p) => p.type).lastIndexOf("text");
      const shown = parts.map((part, index) => {
        if (part.type === "text") {
          return {
            ...part,
            key: `text-${index}`,
            html: html.get(part.content).value,
            lastCard: index === lastText,
          };
        }
        return { ...part, key: "status" };
      });
      if (
        lastText === -1 &&
        (this.message.interactions?.length || this.message.guardrail)
      ) {
        shown.push({ type: "text", key: "extras", html: "", lastCard: true });
      }
      return shown;
    },
    formattedTime() {
      if (!this.message.created_at) return "";
      return dayjs(this.message.created_at).format("h:mm A");
    },
  },
  watch: {
    // Kept while an answer streams, so a token re-renders only its own part.
    latestCitations: {
      immediate: true,
      handler(next) {
        if (next.signature !== this.citations?.signature) {
          this.citations = markRaw(next);
        }
      },
    },
    "message.content": {
      immediate: true,
      handler(value) {
        this.scheduleStreamRender(value || "");
      },
    },
    // The saved message arrives after the last token, carrying the charts it
    // references. Re-resolving then swaps a fetched URL for the message's own.
    "message.attachments": {
      handler() {
        this.artifacts.resolveAttachments(this.displayContent);
        this.artifacts.preloadContentImages(this.displayContent);
      },
    },
    // The handle -> id mapping, which lands just before the first token. Until
    // it does, every reference in the answer is a position nothing can look up.
    "message.artifacts": {
      handler() {
        this.artifacts.resolveAttachments(this.displayContent);
      },
    },
  },
  beforeUnmount() {
    if (this._renderTimer) clearTimeout(this._renderTimer);
    clearTimeout(this._cardTimer);
    clearTimeout(this._litTimer);
    window.removeEventListener("scroll", this.hideCard, true);
  },
  methods: {
    openLabel,
    linkAttributes,
    // The card sits outside the answer, so its link is followed here as the answer's are.
    followCardLink(event) {
      this.card = null;
      this.followInternalLink(event.currentTarget, event);
    },
    // Leave: pin the current height, then animate it to 0 so the row closes evenly.
    pinHeight(el) {
      el.style.height = `${el.offsetHeight}px`;
    },
    collapse(el) {
      void el.offsetHeight;
      el.style.height = "0";
    },
    // Coalesce streamed-content updates to ~90ms so marked.parse + the v-html
    // swap run on a smooth cadence rather than once per token (which thrashes
    // layout and flickers). The final value is always flushed.
    scheduleStreamRender(value) {
      this._pendingContent = value;
      const now = Date.now();
      const elapsed = this._lastRenderAt ? now - this._lastRenderAt : Infinity;
      if (elapsed >= 90) {
        this.flushStreamRender();
      } else if (!this._renderTimer) {
        this._renderTimer = setTimeout(
          () => this.flushStreamRender(),
          90 - elapsed
        );
      }
    },
    flushStreamRender() {
      if (this._renderTimer) {
        clearTimeout(this._renderTimer);
        this._renderTimer = null;
      }
      this._lastRenderAt = Date.now();
      this.displayContent = this._pendingContent;
      this.artifacts.resolveAttachments(this.displayContent);
      this.artifacts.preloadContentImages(this.displayContent);
    },
    /**
     * Each text part's HTML as its own computed, keyed by the text and kept while that
     * text is on screen. A part re-parses only when its text or what it renders from
     * (attachments, images) changes, so while streaming only the growing part does.
     */
    partHtml(parts) {
      const kept = new Map();
      for (const { type, content } of parts) {
        if (type !== "text" || kept.has(content)) continue;
        kept.set(
          content,
          this._partHtml?.get(content) ||
            computed(() => this.renderMarkdown(content))
        );
      }
      this._partHtml = kept;
      return kept;
    },
    renderMarkdown(text) {
      return renderAnswer(text, this.artifacts, {
        renderer: this.markdownRenderer,
        citations: this.citations,
      });
    },
    /**
     * A download whose URL may have expired while the answer sat on screen.
     * The link is re-minted at the moment of the click, so the file is fetched
     * with a URL that is seconds old rather than however old the answer is.
     *
     * Minted, not refreshed: writing the new URL into `resolvedAttachments`
     * would swap the chart above the button back to a skeleton and re-download
     * it, because an image is treated as loaded by URL. Downloading a file has
     * no business disturbing the picture of it.
     */
    async handleContentClick(event) {
      const marker = event.target.closest?.("[data-citation]");
      if (marker) {
        // Without hover there is no card to read first: a tap opens it, and its own
        // "Show in sources" makes the jump.
        if (window.matchMedia?.("(hover: none)").matches) {
          this.showCard(event, { pinned: true });
        } else {
          this.showSources([Number(marker.dataset.citation)], marker);
        }
        return;
      }

      const line = event.target.closest?.("[data-sources]");
      if (line) {
        this.showSources(line.dataset.sources.split(",").map(Number), line);
        return;
      }

      const retry = event.target.closest?.("[data-artifact-retry]");
      if (retry) {
        this.artifacts.retryArtifact(
          retry.dataset.artifactRetry,
          this.displayContent
        );
        return;
      }

      const expand = event.target.closest?.(".table-card__expand");
      if (expand) {
        this.openTable(expand.closest(".table-card"));
        return;
      }

      const chart = event.target.closest?.(".chart-image-wrapper");
      if (chart && !event.target.closest("a[href]")) {
        event.preventDefault();
        this.openChart(chart);
        return;
      }

      const anchor = event.target.closest?.("a[href]");
      if (!anchor) return;
      const id = this.artifacts.artifactIdFor(anchor.getAttribute("href"));
      if (!id) {
        // Not an Oliver artifact, so it may be a cited CRM record; anything else falls
        // through to the href.
        this.followInternalLink(anchor, event);
        return;
      }

      event.preventDefault();
      const minted = await this.artifacts.mintArtifactUrl(id);
      if (!minted && anchor.closest(".file-card")) {
        // A document's old link has expired by now, so the card says so and offers Retry.
        this.artifacts.markUnavailable(id);
        return;
      }
      const url = minted || anchor.href;
      const replay = document.createElement("a");
      replay.href = url;
      if (anchor.hasAttribute("download")) {
        replay.download = anchor.getAttribute("download") || "";
      } else {
        replay.target = "_blank";
        replay.rel = "noopener noreferrer";
      }
      replay.click();
    },
    // Routes a cited CRM record in place. Modifier and non-primary clicks are left to the
    // browser — the stamped href already carries what a new tab needs.
    followInternalLink(anchor, event) {
      if (anchor.dataset.oliverInternal !== "1") return;
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;

      const target = anchor.getAttribute("href");
      if (!target) return;

      event.preventDefault();
      this.$router
        .push(target)
        .then((failure) => {
          // Only on a navigation that happened: announcing a cancelled one would close
          // Oliver and remount the page the user chose not to leave.
          if (failure) return;
          this.$store.dispatch("oliverChatBotV2/noteCitationNavigation");
        })
        .catch(() => {});
    },
    /**
     * Opens the answer's sources at these: one number's, or all of a chart's or table's. The
     * rows stay lit, and remember where they were reached from, until the reader goes back or
     * clicks another.
     */
    showSources(ns, from = null) {
      if (!this.showFooter) return;
      this.sourcesOpen = true;
      this.returnTo = from ? { n: ns[0], marker: from } : null;
      // Cleared first, so a second click on the same number finds its row again.
      this.highlightedSources = [];
      this.$nextTick(() => {
        this.highlightedSources = ns;
      });
    },
    /**
     * Back from a source's row to the answer: to the very number the reader clicked when
     * it is that row, else to the first place the source is used. The number flashes so the
     * eye lands on the sentence.
     */
    returnToMarker(n) {
      const own = this.returnTo?.n === n && this.returnTo.marker;
      const target =
        (own && own.isConnected && own) ||
        this.$el.querySelector(`.message-content [data-citation="${n}"]`);
      if (!target) return;
      // Going back up is leaving the bottom: the chat would otherwise pull the reader down
      // again as the link's text changes, cancelling the scroll.
      this.$emit("stop-following");
      target.scrollIntoView({ block: "center", behavior: "smooth" });
      target.classList.add("citation-marker--lit");
      clearTimeout(this._litTimer);
      this._litTimer = setTimeout(
        () => target.classList.remove("citation-marker--lit"),
        2200
      );
      if (own) {
        this.returnTo = null;
        this.highlightedSources = [];
      }
    },
    /** The card's own jump to the sources: what a click on the number does on a desktop. */
    cardToSources() {
      const { source, marker } = this.card;
      this.card = null;
      this.showSources([source.n], marker);
    },
    /**
     * A number's card, drawn over the page beside it: inside the answer it was cut off by
     * the scrolling bubble and by a table's own scroll. Above the number, or below it when
     * there is no room.
     */
    showCard(event, { pinned = false } = {}) {
      const marker = event.target.closest?.("[data-citation]");
      const source =
        marker && this.citations?.sources[Number(marker.dataset.citation) - 1];
      if (!source) return;
      if (this.card?.pinned && !pinned) return;
      clearTimeout(this._cardTimer);
      const box = marker.getBoundingClientRect();
      const half = Math.min(160, window.innerWidth / 2 - 8);
      const center = box.left + box.width / 2;
      const left = Math.min(
        Math.max(center, half + 8),
        window.innerWidth - half - 8
      );
      const above = box.top > 160;
      this.card = {
        source,
        marker,
        pinned,
        style: {
          ...this.themeVars,
          left: `${left}px`,
          top: `${above ? box.top - 8 : box.bottom + 8}px`,
          transform: `translate(-50%, ${above ? "-100%" : "0"})`,
        },
      };
      window.addEventListener("scroll", this.hideCard, true);
    },
    /**
     * Leaving the number or the card hides the card after a beat, so the pointer can cross
     * the gap between them and use the card's actions; arriving on the card cancels it.
     */
    hideCard(event) {
      const marker = event?.target?.closest?.("[data-citation]");
      if (marker && marker.contains(event.relatedTarget)) return;
      const into = event?.relatedTarget;
      if (
        event?.type !== "scroll" &&
        into &&
        (this.$refs.citationCard?.contains(into) ||
          into.closest?.("[data-citation]"))
      ) {
        return;
      }
      if (this.card?.pinned && event?.type !== "scroll") return;
      clearTimeout(this._cardTimer);
      if (event?.type === "scroll") {
        this.dropCard();
        return;
      }
      this._cardTimer = setTimeout(this.dropCard, 220);
    },
    keepCard() {
      clearTimeout(this._cardTimer);
    },
    dropCard() {
      this.card = null;
      window.removeEventListener("scroll", this.hideCard, true);
    },
    /** The card's rows full screen; the card itself keeps its download. */
    openTable(card) {
      const rows = card?.querySelector(".table-card__scroll");
      if (!rows) return;
      this.$emit("open-table", {
        title:
          card.querySelector(".table-card__title")?.textContent.trim() || "",
        html: rows.innerHTML,
        note:
          card.querySelector(".table-card__actions > span")?.textContent || "",
        // A number clicked full screen opens the answer's sources at it, as it does here.
        cite: (n) => this.showSources([n]),
      });
    },
    /**
     * Hand a chart to the viewer at the size it was actually drawn.
     *
     * The URL on a rendered image is one the browser has already fetched, so it
     * opens instantly and from cache. The artifact id travels with it for the
     * one case that URL can still fail — an answer left on screen past the
     * presigned expiry — which the viewer repairs itself.
     */
    openChart(wrapper) {
      const img = wrapper.querySelector("img");
      if (!img?.src) return;
      this.$emit("open-image", {
        url: img.src,
        alt: img.getAttribute("alt") || "",
        artifactId:
          this.artifacts.artifactIdFor(img.getAttribute("src")) ||
          this.artifacts.artifactIdFor(img.src),
      });
    },
    /**
     * Copies the card it was clicked on: what you click is what you get. The text alone, or
     * with its sources as numbers and a list.
     */
    async handleCopy(part, { withSources = false } = {}) {
      const whole = this.isUser || this.isSystem;
      const text = whole
        ? this.message.content
        : plainTextForCopy(part.content || "", this.artifacts, this.citations, {
            withSources,
          });
      const copied = () => {
        this.copiedKey = part.key;
        setTimeout(() => {
          if (this.copiedKey === part.key) this.copiedKey = null;
        }, 2000);
      };
      try {
        if (whole || typeof ClipboardItem === "undefined") {
          await navigator.clipboard.writeText(text);
        } else {
          // text/plain stays raw markdown so pasting into a markdown/PDF converter still works.
          // The HTML is the card as shown, tables formatted as on screen.
          const html = htmlForCopy(
            this.renderMarkdown(part.content || ""),
            this.citations,
            { withSources }
          );
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([html], { type: "text/html" }),
              "text/plain": new Blob([text], { type: "text/plain" }),
            }),
          ]);
        }
        copied();
      } catch (error) {
        console.error("Failed to copy message:", error);
        try {
          await navigator.clipboard.writeText(text);
          copied();
        } catch (fallbackError) {
          console.error("Fallback copy also failed:", fallbackError);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "./answer-content" as *;
// Leave only: the chips' own fade-in is the enter; it is switched off while leaving.
.follow-ups.follow-ups-fade-leave-active {
  transition: opacity 0.2s ease, height 0.2s ease, margin 0.2s ease;
  overflow: hidden;
  animation: none;
}
.follow-ups.follow-ups-fade-leave-to {
  opacity: 0;
  margin-top: 0;
  margin-bottom: 0;
}
@media (prefers-reduced-motion: reduce) {
  .follow-ups.follow-ups-fade-leave-active {
    transition: none;
  }
}

.message-bubble {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  animation: fadeIn 0.3s ease;

  &--user {
    justify-content: flex-end;

    .bubble-body {
      align-items: flex-end;
      max-width: 75%;
    }

    .message-content {
      background: var(--oliver-user-bubble-bg);
      color: var(--oliver-user-bubble-text);
      border-radius: 16px;
      border: 1px solid var(--oliver-user-bubble-border);
      max-width: 100%;
    }

    .message-timestamp {
      color: var(--oliver-user-bubble-text-muted);
    }

    .copy-btn {
      color: var(--oliver-user-bubble-text);
      opacity: 0.6;
    }
  }

  &--assistant {
    justify-content: flex-start;
    overflow: auto;

    // Fill the row beside the avatar so the answer card is a consistent width
    // (no more shrink-to-content skinny bubble for short answers). Filling it
    // completely also lands the answer's right edge on the composer's, which an
    // 80% cap left 212px short of at full width.
    .bubble-body {
      flex: 1;
      max-width: 100%;
    }

    .message-content {
      background: var(--oliver-assistant-bg);
      color: var(--oliver-text);
      border-radius: 16px;
      box-shadow: var(--oliver-assistant-shadow, none);
    }

    .copy-btn {
      color: var(--oliver-text);
    }
  }

  &--system {
    justify-content: center;

    .bubble-body {
      align-items: center;
    }

    .message-content {
      background: var(--oliver-indicator-bg);
      color: var(--oliver-text-muted);
      border-radius: 12px;
      font-size: 13px;
      padding: 8px 14px;

      .system-message {
        display: flex;
        align-items: center;
        gap: 6px;
        font-style: italic;
      }
    }
  }
}

.bubble-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

// Avatars beside each message row
.bubble-avatar {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-top: 2px;

  &--user {
    overflow: hidden;
    border: 2px solid rgba(255, 255, 255, 0.8);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .bubble-avatar-fallback {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--oliver-accent);
      font-size: 25px;
      color: white;
    }
  }
}

.message-content {
  padding: 12px 16px;
  word-wrap: break-word;
  word-break: break-word;
  position: relative;

  // In the bottom-right corner of every answer card, inside a right margin the text never
  // enters from the first token, so it adds no height, covers nothing and never rewraps the
  // text when a later card arrives; shown on hover or keyboard focus.
  &.message-content--copyable {
    padding-right: 40px;

    // The footer keeps the card's usual right edge.
    .bubble-footer {
      margin-right: -24px;
    }
  }

  .card-actions {
    position: absolute;
    right: 8px;
    bottom: 6px;

    .copy-btn {
      opacity: 0;
    }
  }

  &:hover .card-actions .copy-btn,
  &:focus-within .card-actions .copy-btn {
    opacity: 0.45;
  }

  .card-actions .copy-btn:hover,
  .card-actions .copy-btn:focus-visible {
    opacity: 1;
  }
}

.bubble-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  padding: 0;
}

.bubble-footer-meta {
  display: flex;
  align-items: center;
  gap: 4px;
}

.copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 20px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: all 0.18s ease;
  font-size: 11px;
  flex-shrink: 0;
  opacity: 0.45;

  &:hover {
    opacity: 1;
    background: var(--oliver-accent-light, rgba(0, 0, 0, 0.08));
    border-color: var(--oliver-accent-border, rgba(0, 0, 0, 0.15));
    color: var(--oliver-accent);
  }

  &:active {
    transform: scale(0.95);
  }

  i.fa-check {
    color: var(--oliver-success);
  }
}

// The arrow beside Copy: one pill with it, so it sits close and carries no label.
.copy-btn--more {
  margin-left: -6px;
  padding: 4px 6px;

  i {
    font-size: 9px;
  }
}

// The same card as the theme menu.
.copy-menu {
  width: 290px;
  padding: 12px;
  background: var(--oliver-panel-bg, rgba(255, 255, 255, 0.97));
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.1));
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
}

.copy-menu__label {
  padding: 4px 6px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--oliver-text-muted, rgba(0, 0, 0, 0.4));
}

.copy-menu__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 7px 6px;
  border: none;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover,
  &:focus-visible {
    background: rgba(0, 0, 0, 0.05);
  }
}

.copy-menu__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  i {
    font-size: 12px;
    color: var(--oliver-accent);
  }
}

.copy-menu__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.copy-menu__name {
  font-size: 13px;
  color: var(--oliver-text, #1a1a1a);
}

.copy-menu__hint {
  font-size: 11px;
  color: var(--oliver-text-muted, rgba(0, 0, 0, 0.6));
}

.copy-btn-label {
  font-size: 11px;
  font-weight: 500;
  // line-height 1 + overflow:hidden clipped descenders (e.g. the "p" in
  // "Report" / "Undo Report"); give the line a little vertical room.
  line-height: 1.35;
  letter-spacing: 0.1px;
  display: inline-block;
  text-align: left;
  white-space: nowrap;
}

.bubble-footer-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
  min-width: 0;
}

.system-message {
  font-size: 13px;
  line-height: 1.5;
  position: relative;
}

.assistant-message {
  font-size: 15px;
  line-height: 1.6;
  position: relative;

  // Guardrail refusal: a calm professional-boundary cue (accent bar), not an
  // error state — the message is Oliver working as designed.
  &--guardrail {
    border-left: 3px solid var(--oliver-accent);
    padding-left: 12px;
  }
}

.cut-short {
  margin: 12px 0 0;
  padding-top: 10px;
  border-top: 1px solid var(--oliver-border);
  font-size: 13px;
  color: var(--oliver-text-muted);

  i {
    color: #ef4444;
  }
}

:deep(.markdown-content),
:deep(.system-markdown) {
  @include answer-content;
}

.message-status-icon {
  cursor: help;
  flex-shrink: 0;

  &--failed {
    font-size: 12px;
    color: #ef4444;
  }

  &--pending {
    font-size: 11px;
    color: #999;
    animation: pulse 1.5s ease-in-out infinite;
  }
}

.message-timestamp {
  font-size: 11px;
  color: var(--oliver-text-muted);
  white-space: nowrap;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@media (max-width: 768px) {
  .message-bubble {
    margin-bottom: 18px;
    gap: 8px;
  }

  .message-bubble--user .bubble-body {
    max-width: 85%;
  }

  .message-content {
    padding: 14px 18px;
    font-size: 16px;
  }

  .assistant-message {
    font-size: 16px;
  }

  .bubble-avatar {
    width: 64px;
    height: 64px;
  }

  :deep(.markdown-content) {
    h1 {
      font-size: 24px;
    }
    h2 {
      font-size: 22px;
    }
    h3 {
      font-size: 20px;
    }
    h4 {
      font-size: 18px;
    }

    code {
      font-size: 14px;
    }

    pre {
      padding: 14px;
    }
  }

  .message-timestamp {
    font-size: 12px;
  }

  .copy-btn {
    font-size: 13px;
    padding: 4px 8px 4px 6px;
  }

  .copy-btn-label {
    font-size: 13px;
  }
}

@container (max-width: 480px) {
  .message-bubble--assistant {
    gap: 0;

    .bubble-avatar {
      display: none;
    }

    .message-content {
      padding: 10px 10px 8px;
    }

    .message-content--copyable {
      padding-right: 10px;

      .bubble-footer {
        margin-right: 0;
      }
    }
  }

  .message-bubble--user .bubble-body {
    max-width: 92%;
  }
}
</style>

<!-- Global (non-scoped): citation markers are injected via v-html into rendered
     markdown, and their card is teleported to <body>; scoped styles reach neither. -->
<style lang="scss">
// The copy menu draws its own card; floating-vue's box around it would show at the corners.
.oliver-copy-popper.v-popper--theme-dropdown {
  .v-popper__inner {
    overflow: visible;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
  }

  .v-popper__arrow-container {
    display: none;
  }
}

// A source's number in an answer; its card shows on hover or keyboard focus.
.citation-marker {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin: 0 1px;
  padding: 0 5px;
  border-radius: 9px;
  border: 1px solid rgba(99, 179, 237, 0.35);
  background: rgba(99, 179, 237, 0.12);
  color: var(--oliver-link-color, #2b6cb0);
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  // The card breaks words anywhere; "12" must never split across two lines.
  white-space: nowrap;
  vertical-align: 2px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(99, 179, 237, 0.24);
  }

  &:focus-visible {
    outline: 2px solid var(--oliver-link-color, #2b6cb0);
    outline-offset: 2px;
  }
}

// The number the reader came back to, for a moment, so the eye lands on its sentence.
.citation-marker--lit {
  box-shadow: 0 0 0 3px rgba(99, 179, 237, 0.55);
  transition: box-shadow 0.3s ease;
}

// Teleported to <body> and placed by MessageBubble's showCard.
.citation-card {
  position: fixed;
  width: max-content;
  max-width: min(320px, 80vw);
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.08));
  background: var(--oliver-assistant-solid, #ffffff);
  color: var(--oliver-text);
  box-shadow: 0 6px 24px rgba(60, 40, 20, 0.16);
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  text-align: left;
  white-space: normal;
  z-index: 10000;

  // The 8px between the number and the card, on whichever side the card sits: crossing it
  // counts as being on the card, so the pointer reaches the actions.
  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 12px;
  }

  &::before {
    top: -12px;
  }

  &::after {
    bottom: -12px;
  }

  &__label {
    display: block;
    margin-bottom: 2px;
    font-weight: 600;
    color: var(--oliver-link-color, #2b6cb0);
    overflow-wrap: anywhere;
  }

  &__quote {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: var(--oliver-text-muted);
    font-style: italic;
  }

  // Open the record, or go to its row: what a reader does next without leaving the text.
  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.08));
    font-size: 12px;
    font-weight: 600;

    a,
    button {
      display: inline-flex;
      align-items: center;
      min-height: 28px;
      padding: 0;
      border: 0;
      background: none;
      font: inherit;
      font-weight: 600;
      color: var(--oliver-link-color, #2b6cb0);
      text-decoration: none;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__close {
    margin-left: auto;
    color: var(--oliver-text-muted) !important;
    font-weight: 500 !important;
  }
}
</style>
